import * as React from 'react';
import { UserContext } from '@app/Context/UserContext';
import ReportDataHelper from '@app/Service/Report/ReportDataHelper';
import ContractInterface from '@app/Interface/Report/ContractInterface';
import ItemLoader from '@app/Components/ItemLoader';
import TabulatorMutators from '@app/utils/TabulatorMutators';
import moment from 'moment-timezone';
import { useMediaQuery } from '@react-hook/media-query';

import SCTabulator from '@app/utils/SCTabulator';
import options from '@app/utils/SCTabulatorCommonOptions';
import TabulatorAccessors from '@app/utils/TabulatorAccessors';

interface FCPOpenContractsReportProps {
    pageId: number,
    setLastUpdatedText: React.Dispatch<React.SetStateAction<string>>;
    setReportTableRef: React.Dispatch<React.SetStateAction<React.MutableRefObject<null> | null>>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    loading: boolean;
}

const FCPOpenContractsReport: React.FC<FCPOpenContractsReportProps> = ({ pageId, setLastUpdatedText, setReportTableRef, setLoading, loading }) => {
    // In order to get tabulator to work with moment-timezone
    // we need to add moment to the global namespace.
    // see: https://stackoverflow.com/a/54135475/8038146
    window.moment = moment;

    const isSmallScreen = useMediaQuery('only screen and (max-width: 800px)');

    const { userState } = React.useContext(UserContext);

    // Update the parent report page's download CSV function to tap into our report's tabulator ref
    const tableRef = React.useCallback(node => {
        setReportTableRef({current: node});
    }, []);
    const [reportData, setReportData] = React.useState<ContractInterface[]>([]);

    // TODO: Will we eventually add search? Requirements say that's a future phase.
    const tabulatorColumns = [
        {
            title: 'Contract Date', field: 'Contract_Date', formatter: 'datetime', formatterParams: {
                outputFormat: 'MMM-YYYY',
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        { title: 'Description', field: 'Contract_Description', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        {
            title: 'Transaction Date', field: 'Transaction_Date', formatter: 'datetime', formatterParams: {
                outputFormat: 'MM/DD/YYYY',
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'Contract Volume', field: 'Milk_Volume', formatter: 'money', formatterParams: {
                precision: false
            }, bottomCalc: 'sum', bottomCalcFormatter: 'money', bottomCalcFormatterParams: { precision: false }, 
            accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'Contract Price', field: 'Offer_Price', mutator: TabulatorMutators.timesHundredMutator, formatter: 'money', formatterParams: {
                symbol: '$'
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
    ];

    // Set last updated text
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        ReportDataHelper
            .getTableUpdatedText(controller, 'ndaWeb_FcpOpen')
            .then(text => { if (mounted) setLastUpdatedText(text) });

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, []);

    // When the page, user, or FPU change, fetch new report data
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        async function fetchReportData() {
            if (userState.user?.currentFpuId > 0) {
                const rows = await ReportDataHelper.getContractData(controller, userState.user.currentFpuId, pageId, false);
                if (mounted) {
                    setReportData(rows);
                    setLoading(false);
                }
            } else {
                if (mounted) {
                    setReportData([]);
                    setLoading(false);
                }
            }
        }

        setLoading(true);
        fetchReportData();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [pageId, userState.user.id, userState.user.currentFpuId]);

    if (loading) {
        return <ItemLoader />;
    }

    if (reportData.length === 0) {
        return <p><em>No report data to show for the selected producer</em></p>;
    }

    return (
        <>
            <SCTabulator
                tableRef={tableRef}
                data={reportData}
                columns={tabulatorColumns}
                layout={isSmallScreen ? 'fitData' : 'fitColumns'}
                options={{
                    ...options('nda-report-fcp-open-contracts-sc', true, false)
                }}
            />
        </>
    );
};

export default FCPOpenContractsReport;